import Core                   from "@/core/Core";
import BaseService            from '@/core/services/BaseService';
import Manufacturer           from '@/domains/Manufacturers/Manufacturer';
import ManufacturersDAO       from '@/domains/Manufacturers/ManufacturersDAO';
import ContextMenuItem        from "@/lib/components/VContextMenu/ContextMenuItem";

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';

class ManufacturersService extends BaseService {
  static async get(id: number): Promise<Manufacturer | null> {
    const list = await this.getList({ ids: [id] });
    return list[0] ?? null;
  }

  static async getListPaginated(
    params?: Parameters<typeof ManufacturersDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    }
  ): Promise<{ data: Manufacturer[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await ManufacturersDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Manufacturer.fromServer),
      pagination: response.meta.pagination,
    };
  }

  static async getList(
    params?: Parameters<typeof ManufacturersDAO.getList>[0]
  ): Promise<Manufacturer[]> {
    return (await this.getListPaginated(params)).data;
  }

  /** Поиск производителей по паттерну */
  static async search(pattern: string): Promise<Manufacturer[]> {
    if (!pattern || pattern.length === 0) {
      return [];
    }

    return this.getList({ pattern });
  }

  static async add(manufacturer: Manufacturer): Promise<boolean> {
    const response = await ManufacturersDAO.add(manufacturer);

    if (response.ok) {
      Core.showNotification.success('Производитель создан');
    }

    return response.ok;
  }

  static async update(manufacturer: Manufacturer): Promise<boolean> {
    const response = await ManufacturersDAO.update(manufacturer.id, manufacturer.name);

    if (response.ok) {
      Core.showNotification.success('Производитель обновлён');
    }

    return response.ok;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await ManufacturersDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Производитель удалён');
    }

    return response.ok;
  }

  static async deleteWithConfirmation(manufacturer: Manufacturer): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить этого производителя?',
      `Производитель ${manufacturer.name}`,
    );

    if (confirmed === true) {
      const isDeleted = await this.delete(manufacturer.id);

      if (isDeleted === true) {
        manufacturer.isDeleted = true;
      }

      return isDeleted;
    }
    else {
      return false;
    }
  }

  /**
   * Открыть карточку производителя. null — создание нового.
   * Промис резолвится по закрытию карточки.
   */
  static showCard(manufacturer: Manufacturer | null): Promise<boolean> {
    return Core.dialogs.ManufacturerCard.showAsPromise({ manufacturer });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (manufacturer: Manufacturer) => { return this.showCard(manufacturer); },
        null,
        false,
        true,
      ),
      new ContextMenuItem(
        'История действий',
        'history',
        (manufacturer: Manufacturer) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'manufacturer', entityId: manufacturer.id, showAddComment: false }); },
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (manufacturer: Manufacturer) => { return this.deleteWithConfirmation(manufacturer); },
        null,
        false,
        true,
      ),
    ];
  }
}

export default ManufacturersService;